import React, { useContext, useState, useEffect, useReducer, useRef } from "react";
import { isSameDay, parseISO, format } from "date-fns";
import clsx from "clsx";
import { isNil } from "lodash";
import { blue, green } from "@material-ui/core/colors";
import {
  Button,
  Divider,
  Typography,
  IconButton,
  makeStyles,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@material-ui/core";

import {
  AccessTime,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  Facebook,
  Instagram,
  Reply,
  WhatsApp,
  Launch,
  Chat as ChatIcon,
  Person as PersonIcon,
  FileCopy as FileCopyIcon
} from "@material-ui/icons";
import LockIcon from '@material-ui/icons/Lock';
import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import AgentModeIndicator from "../AgentModeIndicator";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";
import whatsBackgroundDark from "../../assets/wa-background-dark.png";
import YouTubePreview from "../ModalYoutubeCors";
import PdfPreview from "../PdfPreview";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageContext } from "../../context/ForwarMessage/ForwardMessageContext";
import AdMetaPreview from "../AdMetaPreview";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import SelectMessageCheckbox from "./SelectMessageCheckbox";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { AuthContext } from "../../context/Auth/AuthContext";
import { QueueSelectedContext } from "../../context/QueuesSelected/QueuesSelectedContext";
import AudioModal from "../AudioModal";
import { CircularProgress } from "@material-ui/core";
import { useParams, useHistory } from 'react-router-dom';
import Template from "./templates";
import { usePdfViewer } from "../../hooks/usePdfViewer";
import MessageReaction from "./MessageReaction";
import ReactionPicker from "../ReactionPicker";
import useMessageReactions from "../../hooks/useMessageReactions";
import ContactModal from "../ContactModal";
import ShowTicketOpen from "../ShowTicketOpenModal";
import AcceptTicketWithoutQueue from "../AcceptTicketWithoutQueueModal";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastUtils from "../../utils/toastUtils";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    minWidth: 300,
    minHeight: 200,
  },

  currentTick: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "95%",
    backgroundColor: theme.palette.primary.main,
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "1px 5px 10px #b3b3b3",
  },

  currentTicktText: {
    color: theme.palette.primary,
    fontWeight: 'bold',
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  messagesList: {
    backgroundImage: theme.mode === 'light' ? `url(${whatsBackground})` : `url(${whatsBackgroundDark})`,
    backgroundColor: theme.mode === 'light' ? "transparent" : "#0b0b0d",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 30px 20px",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  dragElement: {
    background: theme.mode === 'light' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
    position: "absolute",
    width: "100%",
    height: "100%",
    zIndex: 999999,
    textAlign: "center",
    fontSize: "3em",
    border: theme.mode === 'light' ? "5px dashed #333" : "5px dashed #ccc",
    color: theme.mode === 'light' ? '#333' : '#ccc',
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  circleLoading: {
    color: blue[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
      zIndex: 10,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: theme.mode === 'light' ? "#ffffff" : "#202c33",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.mode === 'light' ? "#f0f0f0" : "#1d282f",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#388aff",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
      zIndex: 10,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: theme.mode === 'light' ? "#dcf8c6" : "#005c4b",
    color: theme.mode === 'light' ? "#303030" : "#ffffff",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  messageRightPrivate: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
      zIndex: 10,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: "#F0E68C",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.mode === 'light' ? "0 1px 1px #b3b3b3" : "0 1px 1px #000000"
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.mode === 'light' ? "#cfe9ba" : "#025144",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: theme.mode === "light" ? "#555" : "#ccc",
    zIndex: 10,
    backgroundColor: theme.mode === "light" ? "rgba(255, 255, 255, 0.85)" : "rgba(30, 30, 30, 0.85)",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.25)",
    borderRadius: "50%",
    padding: 2,
    margin: 2,
    "&:hover, &.Mui-focusVisible": {
      backgroundColor: theme.mode === "light" ? "rgba(255, 255, 255, 0.98)" : "rgba(45, 45, 45, 0.98)",
    },
  },

  messageContactWrapper: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
    padding: "2px 6px 2px 2px",
    borderRadius: 16,
    marginBottom: 4,
    maxWidth: "calc(100% - 28px)",
    transition: "background-color 0.15s ease",
    userSelect: "none",
    "&:hover": {
      backgroundColor: theme.mode === "light" ? "rgba(0, 0, 0, 0.06)" : "rgba(255, 255, 255, 0.1)",
      "& $messageContactName": {
        textDecoration: "underline",
      },
    },
    "&:active": {
      transform: "scale(0.98)",
    },
  },

  messageContactAvatar: {
    width: 22,
    height: 22,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: theme.palette.primary.main,
    color: "#ffffff",
    flexShrink: 0,
  },

  messageContactName: {
    display: "inline-block",
    color: "#6bcbef",
    fontWeight: 500,
    fontSize: "0.82rem",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    // ✅ CORREÇÃO: objectFit removido para vídeos funcionarem melhor
    // objectFit: "cover", // Removido pois pode causar problemas em vídeos
    width: 400,
    height: "auto",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    // ✅ CORREÇÃO: Adicionar estilos específicos para vídeo
    "&[controls]": {
      objectFit: "contain", // Para vídeos, usar contain em vez de cover
    }
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
  },

  forwardMessage: {
    fontSize: 12,
    fontStyle: "italic",
    color: "#888",
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginBottom: 4,
    userSelect: "none",
  },

  forwardMessageIcon: {
    fontSize: 15,
    color: "#888",
    transform: "scaleX(-1)",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: blue[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  ackPlayedIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },
  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
    color: theme.mode === "light" ? theme.palette.light : theme.palette.dark,
  },

  messageCenter: {
    marginTop: 5,
    alignItems: "center",
    verticalAlign: "center",
    alignContent: "center",
    backgroundColor: "#E1F5FEEB",
    fontSize: "12px",
    minWidth: 100,
    maxWidth: 270,
    color: "#272727",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  deletedMessage: {
    color: '#f55d65'
  },
  
  messageButton: {
    display: "flex",
    width: "100%",
    textTransform: "none",
    margin: "auto",
    marginTop: "4px",
  },

  // Estilos para reações
  reactionWrapper: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    marginTop: "4px",
    marginBottom: "2px",
  },

  reactionBubble: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    borderRadius: "12px",
    padding: "2px 6px",
    fontSize: "12px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.1)",
      transform: "scale(1.05)",
    },
  },

  reactionBubbleActive: {
    backgroundColor: `${blue[100]} !important`,
    border: `1px solid ${blue[300]} !important`,
    color: blue[600],
    "&:hover": {
      backgroundColor: `${blue[200]} !important`,
    },
  },

  reactionEmoji: {
    fontSize: "14px",
    lineHeight: 1,
  },

  reactionCount: {
    marginLeft: "4px",
    fontSize: "11px",
    fontWeight: 500,
    color: "inherit",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];
    const updatedState = [...state];

    messages.forEach((message) => {
      const messageIndex = updatedState.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        updatedState[messageIndex] = { ...message };
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...updatedState];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      // Atualizar mensagem existente de forma imutável
      const newState = [...state];
      newState[messageIndex] = { ...newMessage };
      return newState;
    } else {
      // Adicionar nova mensagem de forma imutável
      return [...state, newMessage];
    }
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      // ✅ CORREÇÃO: Criar novo array com objeto atualizado (imutável)
      const newState = [...state];
      newState[messageIndex] = { ...messageToUpdate };
      return newState;
    }

    return state;
  }

  if (action.type === "DELETE_MESSAGE") {
    return state.filter((message) => message.id !== action.payload);
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const MessagesList = ({
  ticket,
  ticketIdProp,
  isGroup,
  onDrop,
  whatsappId,
  queueId,
  channel,
  ticketStatus,
  agentMode = "PROMPT",
  agentConfig = {},
  onAgentTakeover
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const history = useHistory();
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const { ticketId } = useParams();

  const currentTicketId = useRef(ticketId);
  const { getAll } = useCompanySettings();
  const [dragActive, setDragActive] = useState(false);
  const [dragTimeout, setDragTimeout] = useState(null);

  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);

  const [lgpdDeleteMessage, setLGPDDeleteMessage] = useState(false);
  const { selectedQueuesMessage } = useContext(QueueSelectedContext);
  
  // Agent mode states
  const [isAgentActive, setIsAgentActive] = useState(false);
  const [agentIsResponding, setAgentIsResponding] = useState(false);

  // Hook simplificado para PDF
  const {
    downloadPdf,
    extractPdfInfoFromMessage,
    isPdfUrl
  } = usePdfViewer();

  const { showSelectMessageCheckbox } = useContext(ForwardMessageContext);

  // Estados para reações
  const [reactionPickerAnchor, setReactionPickerAnchor] = useState(null);
  const [reactionPickerOpen, setReactionPickerOpen] = useState(false);
  const [selectedMessageForReaction, setSelectedMessageForReaction] = useState(null);
  const { addReaction } = useMessageReactions();
  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;
  const { setTabOpen } = useContext(TicketsContext);

  const [contactMenuAnchor, setContactMenuAnchor] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactAlertOpen, setContactAlertOpen] = useState(false);
  const [contactUserTicketOpen, setContactUserTicketOpen] = useState("");
  const [contactQueueTicketOpen, setContactQueueTicketOpen] = useState("");
  const [contactAcceptTicketWithoutQueueOpen, setContactAcceptTicketWithoutQueueOpen] = useState(false);
  const [contactTicketOpen, setContactTicketOpen] = useState(null);

  useEffect(() => {
    async function fetchData() {
      const settings = await getAll(companyId);

      let settinglgpdDeleteMessage;
      let settingEnableLGPD;

      for (const [key, value] of Object.entries(settings)) {
        if (key === "lgpdDeleteMessage") settinglgpdDeleteMessage = value
        if (key === "enableLGPD") settingEnableLGPD = value
      }
      if (settingEnableLGPD === "enabled" && settinglgpdDeleteMessage === "enabled") {
        setLGPDDeleteMessage(true);
      }
    }
    fetchData();
  }, [])

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    currentTicketId.current = ticketId;
  }, [ticketId, selectedQueuesMessage]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (ticketId === "undefined") {
          history.push("/tickets");
          return;
        }
        if (isNil(ticketId)) return;
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber, selectedQueues: JSON.stringify(selectedQueuesMessage) },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
            setLoadingMore(false);
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
          setLoadingMore(false);
        }
      };

      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId, selectedQueuesMessage]);

  useEffect(() => {
    if (ticketId === "undefined") {
      return;
    }

    const companyId = user.companyId;

    const connectEventMessagesList = () => {
      socket.emit("joinChatBox", `${ticketId}`);
    }

    const onAppMessageMessagesList = (data) => {
      // ✅ CORREÇÃO: Verificar múltiplas estruturas para compatibilidade
      const messageTicketUuid = data.ticket?.uuid || data.message?.ticket?.uuid;
      const messageTicketId = data.ticket?.id || data.message?.ticket?.id || data.message?.ticketId;

      // Verificar tanto UUID quanto ID para máxima compatibilidade
      const isCurrentTicket = (messageTicketUuid && messageTicketUuid === ticketId) ||
                             (messageTicketId && messageTicketId.toString() === ticketId);

      if (data.action === "create" && isCurrentTicket) {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update" && isCurrentTicket) {
        // Garantir que as reações sejam atualizadas corretamente
        const updatedMessage = {
          ...data.message,
          reactions: data.message.reactions || [],
          updatedAt: new Date().toISOString()
        };
        dispatch({ type: "UPDATE_MESSAGE", payload: updatedMessage });
      }

      if (data.action === "delete" && isCurrentTicket) {
        // O ID da mensagem pode vir em data.messageId ou data.message.id
        const messageId = data.messageId || data.message?.id;
        if (messageId) {
          dispatch({ type: "DELETE_MESSAGE", payload: messageId });
        }
      }
    }
    socket.on("connect", connectEventMessagesList);
    socket.on(`company-${companyId}-appMessage`, onAppMessageMessagesList);

    return () => {
      socket.emit("joinChatBoxLeave", `${ticketId}`)
      socket.off("connect", connectEventMessagesList);
      socket.off(`company-${companyId}-appMessage`, onAppMessageMessagesList);
    };

  }, [ticketId]);

  useEffect(() => {
    return () => {
      if (dragTimeout) {
        clearTimeout(dragTimeout);
      }
    };
  }, [dragTimeout]);

  const loadMore = () => {
    if (loadingMore) return;
    setLoadingMore(true);
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      if (lastMessageRef.current) {
        lastMessageRef.current.scrollIntoView({});
      }
    }, 100);
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const hanldeReplyMessage = (e, message) => {
    setAnchorEl(null);
    setReplyingMessage(message);
  };

  // Handlers para reações
  const handleReactionClick = (event, message) => {
    setReactionPickerAnchor(event.currentTarget);
    setSelectedMessageForReaction(message);
    setReactionPickerOpen(true);
  };

  const handleCloseReactionPicker = () => {
    setReactionPickerOpen(false);
    setReactionPickerAnchor(null);
    setSelectedMessageForReaction(null);
  };

  const handleReactionSelect = async (reactionType) => {
    if (!selectedMessageForReaction) return;

    try {
      await addReaction(selectedMessageForReaction.id, reactionType);
    } catch (error) {
      console.error('Erro ao adicionar reação:', error);
    }

    handleCloseReactionPicker();
  };

  // Handler para exclusão de mensagem
  const handleMessageDeleted = (messageId) => {
    dispatch({ type: "DELETE_MESSAGE", payload: messageId });
  };

  // Handlers para interação com contatos em grupos
  const handleContactClick = (e, contact) => {
    e.stopPropagation();
    if (!contact) return;
    setContactMenuAnchor(e.currentTarget);
    setSelectedContact(contact);
  };

  const handleCloseContactMenu = () => {
    setContactMenuAnchor(null);
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setSelectedContact(null);
  };

  const handleStartChatWithContact = async (contact) => {
    if (!contact?.id) return;
    handleCloseContactMenu();

    const targetWhatsappId = whatsappId || ticket?.whatsappId;
    const targetQueueId = queueId || ticket?.queueId;

    try {
      const { data: newTicket } = await api.post("/tickets", {
        contactId: contact.id,
        userId: user?.id,
        status: "open",
        queueId: targetQueueId,
        whatsappId: targetWhatsappId
      });

      setContactTicketOpen(newTicket);
      if (newTicket.queueId === null) {
        setContactAcceptTicketWithoutQueueOpen(true);
      } else {
        if (setTabOpen) {
          setTabOpen("open");
        }
        history.push(`/tickets/${newTicket.uuid}`);
      }
    } catch (err) {
      console.error('❌ Erro ao criar/acessar ticket:', err);

      try {
        let errorData = null;
        if (err.response?.data?.error) {
          if (typeof err.response.data.error === 'string') {
            try {
              errorData = JSON.parse(err.response.data.error);
            } catch (e) {
              errorData = err.response.data.error;
            }
          } else {
            errorData = err.response.data.error;
          }
        }

        if (errorData && typeof errorData === 'object' && errorData.userId !== user?.id) {
          setContactAlertOpen(true);
          setContactUserTicketOpen(errorData.user?.name || 'Usuário desconhecido');
          setContactQueueTicketOpen(errorData.queue?.name || 'Fila desconhecida');
        } else if (errorData && typeof errorData === 'object' && errorData.uuid) {
          setContactAlertOpen(false);
          setContactUserTicketOpen("");
          setContactQueueTicketOpen("");

          if (setTabOpen) {
            setTabOpen(errorData.status);
          }
          history.push(`/tickets/${errorData.uuid}`);
        } else {
          toastError(err);
        }
      } catch (parseError) {
        console.error('❌ Erro ao processar resposta do servidor:', parseError);
        toastError(err);
      }
    }
  };

  const handleOpenContactDetails = () => {
    handleCloseContactMenu();
    setContactModalOpen(true);
  };

  const handleCopyContactNumber = () => {
    if (selectedContact?.number) {
      navigator.clipboard.writeText(selectedContact.number);
      toastUtils.success('Número copiado com sucesso!');
    }
    handleCloseContactMenu();
  };

  // Determina se a mensagem é um template ou mensagem interativa (Meta API Oficial / Baileys)
  const isTemplateMessage = (message) => {
    if (!message) return false;
    const t = message.mediaType;
    if (
      t === "template" ||
      t === "templateMessage" ||
      t === "interactiveMessage" ||
      t === "buttonsMessage" ||
      t === "listMessage" ||
      t === "highlyStructuredMessage" ||
      (typeof message.body === "string" && message.body.includes("||||"))
    ) {
      return true;
    }
    if (message.dataJson) {
      try {
        const raw = typeof message.dataJson === "string" ? JSON.parse(message.dataJson) : message.dataJson;
        const m = raw?.message?.viewOnceMessage?.message ||
                  raw?.message?.viewOnceMessageV2?.message ||
                  raw?.message?.ephemeralMessage?.message ||
                  raw?.message || raw;
        if (
          m?.templateMessage ||
          m?.interactiveMessage ||
          m?.buttonsMessage ||
          m?.listMessage ||
          m?.highlyStructuredMessage
        ) {
          return true;
        }
      } catch (e) {}
    }
    return false;
  };

  const checkMessageMedia = (message) => {
    const isAudioMessage = (message) => {
      if (message.mediaType === "audio") {
        return true;
      }

      if (message.mediaUrl) {
        const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.webm'];
        const url = message.mediaUrl.toLowerCase();
        const hasAudioExtension = audioExtensions.some(ext => url.includes(ext));

        if (hasAudioExtension) {
          return true;
        }
      }

      if (message.body && typeof message.body === 'string') {
        const body = message.body.toLowerCase();
        const isAudioBody = body.includes('áudio gravado') ||
          body.includes('audio_') ||
          body.includes('🎵') ||
          body.includes('arquivo de áudio') ||
          body.includes('mensagem de voz');

        if (isAudioBody) {
          return true;
        }
      }

      return false;
    };

    // Templates e Mensagens Interativas
    if (isTemplateMessage(message)) {
      return <Template message={message} sendReply={sendReply} />;
    }

    // Localização
    else if (message.mediaType === "locationMessage" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|');
      let imageLocation = locationParts[0];
      let linkLocation = locationParts[1];
      let descriptionLocation = locationParts.length > 2 ? locationParts[2] : null;

      return <LocationPreview 
        image={imageLocation} 
        link={linkLocation} 
        description={descriptionLocation} 
      />;
    }

    // Contatos
    else if (message.mediaType === "contactMessage") {
      // ✅ CORREÇÃO: Verificar se é formato simples ou vCard
      if (message.body && message.body.startsWith("📇 Contato:")) {
        // Formato simples: "📇 Contato: Nome|Número"
        const contactData = message.body.replace("📇 Contato: ", "").trim();
        const [contactName, contactNumber] = contactData.split("|");
        
        return <VcardPreview 
          contact={contactName || "Contato"} 
          numbers={contactNumber || ""} 
          queueId={message?.ticket?.queueId} 
          whatsappId={message?.ticket?.whatsappId} 
          channel={channel} 
        />;
      }
      
      // Formato vCard original (BEGIN:VCARD...)
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      
      return <VcardPreview 
        contact={contact || "Contato"} 
        numbers={obj[0]?.number || ""} 
        queueId={message?.ticket?.queueId} 
        whatsappId={message?.ticket?.whatsappId} 
        channel={channel} 
      />;
    }

    else if (message.mediaType === "adMetaPreview") { // Adicionado para renderizar o componente de preview de anúncio
      // ✅ CORREÇÃO: Parse correto dos dados - formato: image|sourceUrl|title|body|messageUser
      let [image, sourceUrl, title, body, messageUser] = message.body.split('|');
      
      // Fallback para messageUser se não estiver presente
      if (!messageUser || messageUser.trim() === "") {
        messageUser = "Olá! Tenho interesse e queria mais informações, por favor.";
      }
      
      return <AdMetaPreview 
        image={image} 
        sourceUrl={sourceUrl} 
        title={title} 
        body={body} 
        messageUser={messageUser} 
      />;
    }

    // PDF e Documentos - SÓ DOWNLOAD
    else if (isPdfUrl(message.mediaUrl, message.body, message.mediaType)) {
      const pdfInfo = extractPdfInfoFromMessage(message);
      
      // Verifica se o body não é vazio e não é igual ao nome do arquivo
      const hasCaption = message.body && message.body.trim() !== '' && 
                         message.body.trim() !== pdfInfo.filename &&
                         !['Outros anexos', 'Documento', 'Anexo', 'documento', 'anexo'].includes(message.body.trim());

      return (
        <>
          <PdfPreview
            url={pdfInfo.url}
            filename={pdfInfo.filename}
            size={pdfInfo.size}
            mediaType={pdfInfo.mediaType}
            onDownload={(url, name) => {
              downloadPdf(url, name);
            }}
          />
          {hasCaption && (
            <Typography variant="body1" style={{ whiteSpace: "pre-wrap", marginTop: 8, padding: "0 10px 8px 10px" }}>
              <MarkdownWrapper>{message.body}</MarkdownWrapper>
            </Typography>
          )}
        </>
      );
    }

    // Áudio
    else if (isAudioMessage(message)) {
      return (
        <div style={{
          width: '100%',
          maxWidth: '300px',
          padding: '8px',
          backgroundColor: 'transparent'
        }}>
          <AudioModal
            url={message.mediaUrl}
            message={message}
          />
        </div>
      );
    }

    // Imagens
    else if (message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    }

    // Vídeos
    else if (message.mediaType === "video") {
      return (
        <div style={{ maxWidth: "400px", width: "100%", position: "relative" }}>
          {/* Loading indicator */}
          {videoLoading && !videoError && (
            <div style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px"
            }}>
              <CircularProgress size={30} />
              <Typography variant="caption" color="textSecondary">
                Carregando vídeo...
              </Typography>
            </div>
          )}
          
          {/* Vídeo player melhorado */}
          <video
            className={classes.messageMedia}
            src={message.mediaUrl}
            controls
            preload="metadata"
            playsInline
            style={{ 
              width: "100%", 
              height: "auto", 
              maxHeight: "300px",
              borderRadius: "8px",
              backgroundColor: "#f0f0f0",
              opacity: videoLoading ? 0.3 : 1,
              transition: "opacity 0.3s ease"
            }}
            onLoadStart={() => {
              setVideoLoading(true);
              setVideoError(false);
            }}
            onLoadedData={() => {
              setVideoLoading(false);
            }}
            onCanPlay={() => {
              setVideoLoading(false);
            }}
            onError={(e) => {
              console.error("❌ Erro ao carregar vídeo:", e);
              setVideoLoading(false);
              setVideoError(true);
            }}
          >
            {/* ✅ CORREÇÃO: Múltiplos formatos para compatibilidade */}
            <source src={message.mediaUrl} type="video/mp4" />
            <source src={message.mediaUrl} type="video/webm" />
            <source src={message.mediaUrl} type="video/ogg" />
            
            {/* Fallback para navegadores antigos */}
            Seu navegador não suporta reprodução de vídeo.
          </video>
          
          {/* Error state */}
          {videoError && (
            <div style={{ 
              padding: "20px", 
              textAlign: "center", 
              backgroundColor: theme.mode === 'light' ? "#f5f5f5" : "#252525",
              borderRadius: "8px",
              color: theme.mode === 'light' ? "#666" : "#ccc",
              marginTop: "8px"
            }}>
              <Typography variant="body2" style={{ marginBottom: "12px" }}>
                <span role="img" aria-label="erro">❌</span> Erro ao carregar vídeo
              </Typography>
              <Button
                startIcon={<GetApp />}
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = message.mediaUrl;
                  link.download = message.body || 'video.mp4';
                  link.click();
                }}
                variant="outlined"
                size="small"
              >
                Baixar Vídeo
              </Button>
            </div>
          )}
        </div>
      );
    }

    // Outros tipos de arquivo
    else if (message.mediaUrl) {
      const fileName = message.body || 'arquivo';
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              variant="outlined"
              onClick={() => downloadPdf(message.mediaUrl, fileName)}
            >
              {fileName.length > 30 ? fileName.substring(0, 30) + '...' : fileName}
            </Button>
          </div>
          <Divider />
        </>
      );
    }

    return null;
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    } else
      if (message.ack === 1) {
        return <Done fontSize="small" className={classes.ackIcons} />;
      } else
        if (message.ack === 2) {
          return <DoneAll fontSize="small" className={classes.ackIcons} />;
        } else
          if (message.ack === 3 || message.ack === 4) {
            return <DoneAll fontSize="small" className={message.mediaType === "audio" ? classes.ackPlayedIcon : classes.ackDoneAllIcon} />;
          } else
            if (message.ack === 5) {
              return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />
            }
  };

  const renderDailyTimestamps = (message, index) => {
    const today = format(new Date(), "dd/MM/yyyy")

    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {today === format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy") ? "HOJE" : format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    } else
      if (index < messagesList.length - 1) {
        let messageDay = parseISO(messagesList[index].createdAt);
        let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

        if (!isSameDay(messageDay, previousMessageDay)) {
          return (
            <span
              className={classes.dailyTimestamp}
              key={`timestamp-${message.id}`}
            >
              <div className={classes.dailyTimestampText}>
                {today === format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy") ? "HOJE" : format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
              </div>
            </span>
          );
        }
      } else
        if (index === messagesList.length - 1) {
          return (
            <div
              key={`ref-${message.id}`}
              ref={lastMessageRef}
              style={{ float: "left", clear: "both" }}
            />
          );
        }
  };

  const renderTicketsSeparator = (message, index) => {
    let lastTicket = messagesList[index - 1]?.ticketId;
    let currentTicket = message.ticketId;

    if (lastTicket !== currentTicket && lastTicket !== undefined) {
      if (message?.ticket?.queue) {
        return (
          <span
            className={classes.currentTick}
            key={`timestamp-${message.id}a`}
          >
            <div
              className={classes.currentTicktText}
              style={{ backgroundColor: message?.ticket?.queue?.color || "grey" }}
            >
              #{i18n.t("ticketsList.called")} {message?.ticketId} - {message?.ticket?.queue?.name}
            </div>

          </span>
        );
      } else {
        return (
          <span
            className={classes.currentTick}
            key={`timestamp-${message.id}b`}
          >
            <div
              className={classes.currentTicktText}
              style={{ backgroundColor: "grey" }}
            >
              #{i18n.t("ticketsList.called")} {message.ticketId} - {i18n.t("ticketsList.noQueue")}
            </div>

          </span>
        );
      }
    }

  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;
      if (messageUser !== previousMessageUser) {
        return (

          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const path = require('path');

  const renderQuotedMessage = (message) => {

    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}

          {message.quotedMsg.mediaType === "audio"
            && (
              <div className={classes.downloadMedia}>
                <AudioModal url={message.quotedMsg.mediaUrl} />
              </div>
            )
          }
          {message.quotedMsg.mediaType === "video"
            && (
              <div style={{ maxWidth: "300px", width: "100%" }}>
                <video
                  className={classes.messageMedia}
                  src={message.quotedMsg.mediaUrl}
                  controls
                  preload="metadata"
                  style={{ 
                    width: "100%", 
                    height: "auto", 
                    maxHeight: "200px",
                    borderRadius: "6px",
                    backgroundColor: "#f0f0f0"
                  }}
                  onError={(e) => {
                    console.error("❌ Erro ao carregar vídeo citado:", e);
                  }}
                >
                  <source src={message.quotedMsg.mediaUrl} type="video/mp4" />
                  <source src={message.quotedMsg.mediaUrl} type="video/webm" />
                  <source src={message.quotedMsg.mediaUrl} type="video/ogg" />
                  <div style={{ padding: "10px", textAlign: "center", fontSize: "12px", color: "#999" }}>
                    <span role="img" aria-label="erro">❌</span> Erro ao carregar vídeo
                  </div>
                </video>
              </div>
            )
          }
          {message.quotedMsg.mediaType === "contactMessage"
            && (
              "Contato"
            )
          }
          {message.quotedMsg.mediaType === "application"
            && (
              <div className={classes.downloadMedia}>
                <Button
                  startIcon={<GetApp />}
                  variant="outlined"
                  target="_blank"
                  href={message.quotedMsg.mediaUrl}
                >
                  Download
                </Button>
              </div>
            )
          }

          {message.quotedMsg.mediaType === "image" ? (
            <ModalImageCors imageUrl={message.quotedMsg.mediaUrl} />
          ) : (
            message.quotedMsg?.body
          )}

        </div>
      </div>
    );
  };

  const handleDrag = event => {
    event.preventDefault();
    event.stopPropagation();

    if (event.type === "dragenter" || event.type === "dragover") {
      const hasFiles = event.dataTransfer &&
        event.dataTransfer.types &&
        (event.dataTransfer.types.includes('Files') ||
          event.dataTransfer.types.includes('application/x-moz-file'));

      if (hasFiles) {
        if (dragTimeout) {
          clearTimeout(dragTimeout);
        }

        const timeout = setTimeout(() => {
          if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
            setDragActive(true);
          }
        }, 100);

        setDragTimeout(timeout);
      }
    } else if (event.type === "dragleave") {
      if (dragTimeout) {
        clearTimeout(dragTimeout);
        setDragTimeout(null);
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;

      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        setDragActive(false);
      }
    }
  }

  const isYouTubeLink = (url) => {
    const youtubeRegex = /(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    return youtubeRegex.test(url);
  };

  const checkMessageIsForwarded = (msg) => {
    if (msg?.isForwarded) return true;
    if (msg?.dataJson) {
      try {
        const data = typeof msg.dataJson === "string" ? JSON.parse(msg.dataJson) : msg.dataJson;
        const contextInfo =
          data?.message?.extendedTextMessage?.contextInfo ||
          data?.message?.imageMessage?.contextInfo ||
          data?.message?.audioMessage?.contextInfo ||
          data?.message?.videoMessage?.contextInfo ||
          data?.message?.documentMessage?.contextInfo ||
          data?.message?.contactMessage?.contextInfo ||
          data?.contextInfo;
        if (contextInfo?.isForwarded === true || (contextInfo?.forwardingScore && contextInfo.forwardingScore > 0)) {
          return true;
        }
      } catch (e) {
        // ignore JSON parse error
      }
    }
    return false;
  };

  const handleDrop = event => {
    event.preventDefault();
    event.stopPropagation();

    if (dragTimeout) {
      clearTimeout(dragTimeout);
      setDragTimeout(null);
    }

    setDragActive(false);

    if (event.dataTransfer.files &&
      event.dataTransfer.files.length > 0 &&
      event.dataTransfer.files[0] instanceof File) {
      if (onDrop) {
        onDrop(event.dataTransfer.files);
      }
    }
  }
  const xmlRegex = /<([^>]+)>/g;
  const boldRegex = /\*(.*?)\*/g;

  const formatXml = (xmlString) => {
    // Verifica se o XML contém a assinatura com nome do atendente
    if (boldRegex.test(xmlString)) {
      // Formata o texto dentro da assinatura em negrito
      xmlString = xmlString.replace(boldRegex, "**$1**");
    }
    return xmlString;
  };

  // ✅ INTERACTIVE BUTTONS IMPLEMENTATION
  const sendReply = async (body) => {
    const targetTicketId = ticketIdProp || ticket?.id || ticketId;
    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      body
    };

    api.post(`/messages/${targetTicketId}`, message).catch((err) => {
      toastError(err);
    });
  };

  const renderReplyButton = (text) => {
    return (
      <Button
        className={classes.messageButton}
        color="primary"
        startIcon={<Reply />}
        onClick={() => {
          sendReply(text);
        }}
      >
        {text}
      </Button>
    );
  };
  
  const renderButtons = (message) => {
    const objects = 
      message?.buttonsMessage?.buttons ||
      message?.listMessage?.sections ||
      message?.templateMessage?.hydratedTemplate?.hydratedButtons ||
      message?.templateMessage?.interactiveMessageTemplate?.nativeFlowMessage?.buttons

    if (!objects) return (<></>);

    return objects.map((item, index) => {
      if (item.urlButton) {
        return (
          <Button
            className={classes.messageButton}
            key={index}
            color="primary"
            startIcon={item.urlButton.displayText === 'Facebook' ? <Facebook /> : item.urlButton.displayText === 'Instagram' ? <Instagram /> : <Launch />}
          >
            <a href={item.urlButton.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
              {item.urlButton.displayText}
            </a>
          </Button>
        );
      } else if (item.quickReplyButton) {
        return renderReplyButton(item.quickReplyButton.displayText);
      } else if (item.type === "RESPONSE" && item.buttonText) {
        return renderReplyButton(item.buttonText.displayText);
      } else if (item.buttonParamsJson) {
        const params = JSON.parse(item.buttonParamsJson);
        if (params?.displayText) {
          return renderReplyButton(params.displayText);
        }
      } else if (item.rows) {
        return item.rows.map((row) => {
          return renderReplyButton(row.title);
        });
      }
         
      return (<></>);
    }
    );
  };

  const renderMessages = () => {

    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        if (message.mediaType === "call_log") {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderTicketsSeparator(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageCenter}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <Tooltip title={`Clique para interagir com ${message.contact?.name || "contato"}`} arrow>
                    <div
                      className={classes.messageContactWrapper}
                      onClick={(e) => handleContactClick(e, message.contact)}
                    >
                      <Avatar
                        src={message.contact?.urlPicture || message.contact?.profilePicUrl}
                        alt={message.contact?.name || "Contato"}
                        className={classes.messageContactAvatar}
                      >
                        {message.contact?.name ? message.contact.name.charAt(0).toUpperCase() : "?"}
                      </Avatar>
                      <span className={classes.messageContactName}>
                        {message.contact?.name}
                      </span>
                    </div>
                  </Tooltip>
                )}

                <div>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                    <path fill="#df3333" d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"></path>
                  </svg> <span>{i18n.t("ticketsList.missedCall")} {format(parseISO(message.createdAt), "HH:mm")}</span>
                </div>
              </div>
            </React.Fragment>
          );
        }

        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderTicketsSeparator(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={classes.messageLeft}
                title={message.queueId && message.queue?.name}
                onDoubleClick={(e) => hanldeReplyMessage(e, message)}
              >
                {showSelectMessageCheckbox && (
                  <SelectMessageCheckbox
                    message={message}
                  />
                )}
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>

                {checkMessageIsForwarded(message) && (
                  <div className={classes.forwardMessage}>
                    <Reply className={classes.forwardMessageIcon} />
                    <span>Encaminhada</span>
                  </div>
                )}
                {isGroup && (
                  <Tooltip title={`Clique para interagir com ${message.contact?.name || "contato"}`} arrow>
                    <div
                      className={classes.messageContactWrapper}
                      onClick={(e) => handleContactClick(e, message.contact)}
                    >
                      <Avatar
                        src={message.contact?.urlPicture || message.contact?.profilePicUrl}
                        alt={message.contact?.name || "Contato"}
                        className={classes.messageContactAvatar}
                      >
                        {message.contact?.name ? message.contact.name.charAt(0).toUpperCase() : "?"}
                      </Avatar>
                      <span className={classes.messageContactName}>
                        {message.contact?.name}
                      </span>
                    </div>
                  </Tooltip>
                )}
                {isYouTubeLink(message.body) && (
                  <>
                    <YouTubePreview videoUrl={message.body} />
                  </>
                )}

                {!lgpdDeleteMessage && message.isDeleted && (
                  <div>
                    <span className={classes.deletedMessage}>
                      <span role="img" aria-label="apagada">🚫</span> Essa mensagem foi apagada pelo contato &nbsp;
                    </span>
                  </div>
                )}

                {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "contactMessage" || isTemplateMessage(message) || message.mediaType === "adMetaPreview"
                ) && checkMessageMedia(message)}
                
                {/* ✅ RENDER INTERACTIVE BUTTONS */}
                {!isTemplateMessage(message) && message.dataJson && renderButtons(JSON.parse(message.dataJson)?.message)}

                <div className={clsx(classes.textContentItem, {
                  [classes.textContentItemDeleted]: message.isDeleted,
                })}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {
                    !isTemplateMessage(message) && message.mediaType !== "adMetaPreview" && (
                      (message.mediaUrl !== null && (message.mediaType === "image" || message.mediaType === "video") && path.basename(message.mediaUrl).trim() !== message.body.trim()) ||
                      (message.mediaType !== "audio" &&
                      message.mediaType !== "image" &&
                      message.mediaType !== "video" &&
                      message.mediaType !== "document" &&
                      message.mediaType !== "application" &&
                      message.mediaType !== "reactionMessage" &&
                      message.mediaType !== "locationMessage" &&
                      message.mediaType !== "contactMessage" &&
                      message.mediaType !== "template" &&
                      message.mediaType !== "interactiveMessage")
                    ) && (
                      <>
                        {xmlRegex.test(message.body) && (
                          <span>{message.body}</span>

                        )}
                        {!xmlRegex.test(message.body) && (
                          <MarkdownWrapper>{(lgpdDeleteMessage && message.isDeleted) ? "🚫 _Mensagem apagada_ " :
                            message.body
                          }</MarkdownWrapper>)}

                      </>

                    )}

                  {message.mediaType === "reactionMessage" && (
                    <span style={{ 
                      fontSize: '0.9em',
                      color: '#666',
                      fontStyle: 'italic',
                      marginLeft: '0px'
                    }}>
                      {message?.contact?.name + " reagiu com " + message.body}
                    </span>
                  )}

                  {message.mediaType === "interactiveMessage" && (
                    <>
                      {(() => {
                        // Extrair informações do body
                        const merchantMatch = message.body.match(/Comerciante:\s*([^\n]+)/);
                        const pixKeyMatch = message.body.match(/Chave PIX:\s*([^\n]+)/);
                        const cnpjMatch = message.body.match(/CNPJ:\s*([^\n]+)/);
                        
                        const merchantName = merchantMatch ? merchantMatch[1].trim() : 'Comerciante';
                        const pixKey = pixKeyMatch ? pixKeyMatch[1].trim() : '';
                        const cnpj = cnpjMatch ? cnpjMatch[1].trim() : '';
                        
                        return (
                          <div style={{
                            backgroundColor: '#1f2c33',
                            padding: '12px',
                            borderRadius: '8px',
                            maxWidth: '300px'
                          }}>
                            {/* Header com logo e nome do comerciante */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              marginBottom: '8px'
                            }}>
                              <div style={{
                                width: '40px',
                                height: '40px',
                                backgroundColor: '#0d1f2d',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                overflow: 'hidden'
                              }}>
                                <img 
                                  src="/pix-logo.png" 
                                  alt="PIX" 
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    objectFit: 'cover'
                                  }}
                                />
                              </div>
                              <div>
                                <div style={{
                                  color: '#e9edef',
                                  fontSize: '16px',
                                  fontWeight: '500'
                                }}>
                                  {merchantName}
                                </div>
                                {cnpj && (
                                  <div style={{
                                    color: '#8696a0',
                                    fontSize: '13px',
                                    marginTop: '2px'
                                  }}>
                                    CNPJ: {cnpj}
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Botão Copiar chave Pix */}
                            {pixKey && (
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(pixKey);
                                  alert('Chave PIX copiada!');
                                }}
                                style={{
                                  backgroundColor: 'transparent',
                                  color: '#00a884',
                                  border: 'none',
                                  borderRadius: '20px',
                                  padding: '10px 16px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  width: '100%',
                                  justifyContent: 'center',
                                  transition: 'background-color 0.2s',
                                  marginTop: '8px'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 168, 132, 0.1)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              >
                                <svg width="18" height="18" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M502.6 70.63l-61.25-61.25C435.4 3.371 427.2 0 418.7 0H255.1c-35.35 0-64 28.66-64 64l.0195 256C192 355.4 220.7 384 256 384h192c35.2 0 64-28.8 64-64V93.25C512 84.77 508.6 76.63 502.6 70.63zM464 320c0 8.836-7.164 16-16 16H255.1c-8.838 0-16-7.164-16-16L239.1 64.13c0-8.836 7.164-16 16-16h128L384 96c0 17.67 14.33 32 32 32h47.1V320zM272 448c0 8.836-7.164 16-16 16H63.1c-8.838 0-16-7.164-16-16L47.98 192.1c0-8.836 7.164-16 16-16H160V128H63.99c-35.35 0-64 28.65-64 64l.0098 256C.002 483.3 28.66 512 64 512h192c35.2 0 64-28.8 64-64v-32h-47.1L272 448z" fill="#00a884"/>
                                </svg>
                                <span>Copiar chave Pix</span>
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  )}

                  <span className={classes.timestamp}>
                    {message.isEdited ? "Editada " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
                <MessageReaction
                  key={`reactions-${message.id}-${JSON.stringify(message.reactions)}`}
                  message={message}
                  classes={classes}
                />
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderTicketsSeparator(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={message.isPrivate ? classes.messageRightPrivate : classes.messageRight}
                title={message.queueId && message.queue?.name}
                onDoubleClick={(e) => hanldeReplyMessage(e, message)}
              >
                {showSelectMessageCheckbox && (
                  <SelectMessageCheckbox
                    message={message}
                  />
                )}

                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {checkMessageIsForwarded(message) && (
                  <div className={classes.forwardMessage}>
                    <Reply className={classes.forwardMessageIcon} />
                    <span>Encaminhada</span>
                  </div>
                )}
                {isYouTubeLink(message.body) && (
                  <>
                    <YouTubePreview videoUrl={message.body} />
                  </>
                )}
                {!lgpdDeleteMessage && message.isDeleted && (
                  <div>
                    <span className={classes.deletedMessage}
                    ><span role="img" aria-label="apagada">🚫</span> Essa mensagem foi apagada &nbsp;
                    </span>
                  </div>
                )}
                {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "contactMessage" || isTemplateMessage(message)
                ) && checkMessageMedia(message)}
                
                {/* ✅ RENDER INTERACTIVE BUTTONS */}
                {!isTemplateMessage(message) && message.dataJson && renderButtons(JSON.parse(message.dataJson)?.message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >

                  {message.quotedMsg && renderQuotedMessage(message)}

                  {
                    !isTemplateMessage(message) && (((message.mediaType === "image" || message.mediaType === "video") && path.basename(message.mediaUrl) === message.body) ||
                    (message.mediaType !== "audio" && message.mediaType !== "document" && message.mediaType !== "application" && message.mediaType != "reactionMessage" && message.mediaType != "locationMessage" && message.mediaType !== "contactMessage" && message.mediaType !== "template" && message.mediaType !== "interactiveMessage")) && (
                      <>
                        {xmlRegex.test(message.body) && (
                          <div>{formatXml(message.body)}</div>

                        )}
                        {!xmlRegex.test(message.body) && (<MarkdownWrapper>{message.body}</MarkdownWrapper>)}

                      </>
                    )}

                  {message.mediaType === "reactionMessage" && (
                    <span style={{ 
                      fontSize: '0.9em',
                      color: '#666',
                      fontStyle: 'italic',
                      marginLeft: '0px'
                    }}>
                      {"Você reagiu com " + message.body}
                    </span>
                  )}

                  {message.mediaType === "interactiveMessage" && (
                    <>
                      {(() => {
                        // Extrair informações do body
                        const merchantMatch = message.body.match(/Comerciante:\s*([^\n]+)/);
                        const pixKeyMatch = message.body.match(/Chave PIX:\s*([^\n]+)/);
                        const cnpjMatch = message.body.match(/CNPJ:\s*([^\n]+)/);
                        
                        const merchantName = merchantMatch ? merchantMatch[1].trim() : 'Comerciante';
                        const pixKey = pixKeyMatch ? pixKeyMatch[1].trim() : '';
                        const cnpj = cnpjMatch ? cnpjMatch[1].trim() : '';
                        
                        return (
                          <div style={{
                            backgroundColor: '#005c4b',
                            padding: '12px',
                            borderRadius: '8px',
                            maxWidth: '300px'
                          }}>
                            {/* Header com logo e nome do comerciante */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              marginBottom: '8px'
                            }}>
                              <div style={{
                                width: '40px',
                                height: '40px',
                                backgroundColor: '#0d1f2d',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                overflow: 'hidden'
                              }}>
                                <img 
                                  src="/pix-logo.png" 
                                  alt="PIX" 
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    objectFit: 'cover'
                                  }}
                                />
                              </div>
                              <div>
                                <div style={{
                                  color: '#e9edef',
                                  fontSize: '16px',
                                  fontWeight: '500'
                                }}>
                                  {merchantName}
                                </div>
                                {cnpj && (
                                  <div style={{
                                    color: '#8696a0',
                                    fontSize: '13px',
                                    marginTop: '2px'
                                  }}>
                                    CNPJ: {cnpj}
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Botão Copiar chave Pix */}
                            {pixKey && (
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(pixKey);
                                  alert('Chave PIX copiada!');
                                }}
                                style={{
                                  backgroundColor: 'transparent',
                                  color: '#00a884',
                                  border: 'none',
                                  borderRadius: '20px',
                                  padding: '10px 16px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  width: '100%',
                                  justifyContent: 'center',
                                  transition: 'background-color 0.2s',
                                  marginTop: '8px'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 168, 132, 0.1)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              >
                                <svg width="18" height="18" viewBox="0 0 512 512" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M502.6 70.63l-61.25-61.25C435.4 3.371 427.2 0 418.7 0H255.1c-35.35 0-64 28.66-64 64l.0195 256C192 355.4 220.7 384 256 384h192c35.2 0 64-28.8 64-64V93.25C512 84.77 508.6 76.63 502.6 70.63zM464 320c0 8.836-7.164 16-16 16H255.1c-8.838 0-16-7.164-16-16L239.1 64.13c0-8.836 7.164-16 16-16h128L384 96c0 17.67 14.33 32 32 32h47.1V320zM272 448c0 8.836-7.164 16-16 16H63.1c-8.838 0-16-7.164-16-16L47.98 192.1c0-8.836 7.164-16 16-16H160V128H63.99c-35.35 0-64 28.65-64 64l.0098 256C.002 483.3 28.66 512 64 512h192c35.2 0 64-28.8 64-64v-32h-47.1L272 448z" fill="#00a884"/>
                                </svg>
                                <span>Copiar chave Pix</span>
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </>
                  )}

                  <span className={classes.timestamp}>
                    {message.isEdited ? "Editada " + format(parseISO(message.createdAt), "HH:mm") : format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                </div>
                <MessageReaction
                  key={`reactions-${message.id}-${JSON.stringify(message.reactions)}`}
                  message={message}
                  classes={classes}
                />
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Diga olá para seu novo contato!</div>;
    }
  };
const shouldBlurMessages = ticketStatus === "pending" && user.allowSeeMessagesInPendingTickets === "disabled";

  return (
    <div className={classes.messagesListWrapper} onDragEnter={handleDrag}>
      {dragActive && <div className={classes.dragElement} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>Solte o arquivo aqui</div>}
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
        isGroup={isGroup}
        whatsappId={whatsappId}
        queueId={queueId}
        onReactionClick={handleReactionClick}
        onMessageDeleted={handleMessageDeleted}
      />
      

<div
  id="messagesList"
  className={classes.messagesList}
  onScroll={handleScroll}
  style={{
    filter: shouldBlurMessages ? "blur(4px)" : "none",
    pointerEvents: shouldBlurMessages ? "none" : "auto"
  }}
>
  {messagesList.length > 0 ? renderMessages() : []}
</div>

      {(channel !== "whatsapp" && channel !== "webchat" && channel !== undefined) && (
        <div
          style={{
            width: "100%",
            display: "flex",
            padding: "10px",
            alignItems: "center",
            backgroundColor: "#E1F3FB",
          }}
        >
          {channel === "facebook" ? (
            <Facebook />
          ) : channel === "instagram" ? (
            <Instagram />
          ) : (
            <WhatsApp />
          )}

          <span>
            Você tem 24h para responder após receber uma mensagem, de acordo
            com as políticas da Meta.
          </span>
        </div>
      )}
      
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}

      <ReactionPicker
        anchorEl={reactionPickerAnchor}
        open={reactionPickerOpen}
        onClose={handleCloseReactionPicker}
        onReactionSelect={handleReactionSelect}
      />

      <Menu
        anchorEl={contactMenuAnchor}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        open={Boolean(contactMenuAnchor)}
        onClose={handleCloseContactMenu}
        autoFocus={false}
      >
        <MenuItem onClick={() => handleStartChatWithContact(selectedContact)}>
          <ListItemIcon style={{ minWidth: 36 }}>
            <ChatIcon fontSize="small" style={{ color: "#25D366" }} />
          </ListItemIcon>
          <ListItemText primary={`Conversar com ${selectedContact?.name || "contato"}`} />
        </MenuItem>
        <MenuItem onClick={handleOpenContactDetails}>
          <ListItemIcon style={{ minWidth: 36 }}>
            <PersonIcon fontSize="small" style={{ color: "#1976d2" }} />
          </ListItemIcon>
          <ListItemText primary="Ver dados do contato" />
        </MenuItem>
        {selectedContact?.number && (
          <MenuItem onClick={handleCopyContactNumber}>
            <ListItemIcon style={{ minWidth: 36 }}>
              <FileCopyIcon fontSize="small" style={{ color: "#757575" }} />
            </ListItemIcon>
            <ListItemText primary={`Copiar número (${selectedContact.number})`} />
          </MenuItem>
        )}
      </Menu>

      {contactModalOpen && (
        <ContactModal
          open={contactModalOpen}
          onClose={handleCloseContactModal}
          contactId={selectedContact?.id}
        />
      )}

      <AcceptTicketWithoutQueue
        modalOpen={contactAcceptTicketWithoutQueueOpen}
        onClose={() => setContactAcceptTicketWithoutQueueOpen(false)}
        ticket={contactTicketOpen}
        ticketId={contactTicketOpen?.id}
      />

      <ShowTicketOpen
        isOpen={contactAlertOpen}
        handleClose={() => {
          setContactAlertOpen(false);
          setContactUserTicketOpen("");
          setContactQueueTicketOpen("");
        }}
        user={contactUserTicketOpen}
        queue={contactQueueTicketOpen}
      />
    </div>
  );
};

export default MessagesList;